import { useQuery } from '@tanstack/react-query';

import { QUERY_STALE_TIME, isSupabaseConfigured } from '@/config';
import { queryKeys } from '@/lib/data/keys';
import {
  getCompetitionHistory,
  getScores365Bracket,
  getScores365Standings,
  parseWcHistory,
  WC_365_COMPETITION_ID,
  type BracketGame,
  type WcHistoryFinal,
  type WcStandings,
} from '@/lib/data/provider';

/**
 * A competition's past finals/winners (defaults to the World Cup). The raw 365
 * history blob is parsed into a clean {@link WcHistoryFinal}[] (newest first)
 * here so the WcHistory card stays presentational. History rarely changes, so
 * it caches on the long `reference` window.
 */
export function useCompetitionHistory(
  providerId: number = WC_365_COMPETITION_ID,
) {
  return useQuery<WcHistoryFinal[]>({
    queryKey: queryKeys.competitionHistory(providerId),
    queryFn: async () => parseWcHistory(await getCompetitionHistory(providerId)),
    staleTime: QUERY_STALE_TIME.reference,
    enabled: isSupabaseConfigured,
  });
}

/**
 * The RICH 365scores WC group standings ({@link WcStandings}: 12 groups A–L +
 * the qualification destination legend). Returns null until the data sync has
 * run, so consumers silent-hide on empty. Standings move at most once per
 * matchday, so they cache on the long `reference` window.
 */
export function useScores365Standings(
  providerId: number = WC_365_COMPETITION_ID,
) {
  return useQuery<WcStandings | null>({
    queryKey: queryKeys.scores365Standings(providerId),
    queryFn: () => getScores365Standings(providerId),
    staleTime: QUERY_STALE_TIME.reference,
    enabled: isSupabaseConfigured,
  });
}

/**
 * The 365scores knockout-bracket games ({@link BracketGame}[]). Until 365 emits
 * `stageNum >= 2` ties these are all group-stage games; the bracket UI filters
 * to the knockout rounds and shows a placeholder until they appear — no code
 * change needed. Caches on the `reference` window like the other 365 reads.
 */
export function useScores365Bracket(
  providerId: number = WC_365_COMPETITION_ID,
) {
  return useQuery<BracketGame[]>({
    queryKey: queryKeys.scores365Bracket(providerId),
    queryFn: () => getScores365Bracket(providerId),
    staleTime: QUERY_STALE_TIME.reference,
    enabled: isSupabaseConfigured,
  });
}
